import { useEffect, useRef, useState } from "react";
import {
  MdCalendarToday,
  MdDelete,
  MdEdit,
  MdInfo,
  MdLightbulb,
  MdLink,
  MdSync,
  MdWarningAmber,
} from "react-icons/md";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";
import { FieldGroup, FormModal, Input, SelectField, Textarea } from "../../components/ui";
import HighlightText from "../../shared/HighlightText";
import { formatDateFr } from "../../shared/dateFormat";
import { DOC_TYPES, DOC_TYPES_WITH_REF, DocTypeBadge } from "../../shared/docTypes";
import { useDocTypesEnabled } from "../../shared/DocTypesEnabledContext";
import { habColor, HabBadge, HABILITATIONS } from "../../shared/habilitations";
import { categoryBadgeStylesFromCategories, DEFAULT_FIRST_ORGANIZATION_CATEGORY_LABEL, isLockedOrganizationCategory } from "../../shared/organizationCategories";
import { APP_FONT_FAMILY, COLORS, RADIUS, SPACING } from "../../shared/theme";

// ─── CONSTANTES RÉFÉRENTIELS ─────────────────────────────────────────────────

const RISQUE_ST = ["Faible", "Modéré", "Élevé", "Critique"];
const PAYS_ADEQUATION = ["France", "Allemagne", "Pays-Bas", "Irlande", "Belgique", "Suède", "Espagne", "Italie", "Canada", "Japon", "Suisse", "Royaume-Uni", "USA", "Inde", "Chine", "Brésil", "Australie", "Autre"];

const risqueColor = {
  "Faible":    { bg: "#d1fae5", text: "#065f46", border: "#6ee7b7" },
  "Modéré":    { bg: "#fef3c7", text: "#92400e", border: "#fcd34d" },
  "Élevé":     { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  "Critique":  { bg: "#fdf2f8", text: "#9d174d", border: "#f9a8d4" },
};

const transfertColor = {
  true:  { label: "Hors EEE", bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  false: { label: "Dans l'EEE ✓", bg: "#d1fae5", text: "#065f46", border: "#6ee7b7" },
};

// ─── SOUS-COMPOSANTS RÉFÉRENTIELS ─────────────────────────────────────────────

const emptyPersonne = { prenom: "", nom: "", habilitation: "DPO", fonction: "", email: "", perimetre: "" };

function RefPersonnes({ personnes, setPersonnes, onCreatePersonne, onUpdatePersonne, onDeletePersonne, searchHighlight }) {
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const highlightRef = useRef(null);
  const nextId = useRef(100);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const highlightId = searchHighlight?.id != null ? Number(searchHighlight.id) : null;
  const searchTerm = searchHighlight?.searchTerm || null;

  useEffect(() => {
    if (highlightId != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId]);

  const save = async () => {
    setSaving(true);
    if (editing) {
      if (onUpdatePersonne) {
        await onUpdatePersonne(editing.id, form);
      } else {
        setPersonnes(ps => ps.map(p => p.id === editing.id ? { ...form, id: editing.id } : p));
      }
    } else if (onCreatePersonne) {
      await onCreatePersonne(form);
    } else {
      setPersonnes(ps => [...ps, { ...form, id: nextId.current++ }]);
    }
    setSaving(false);
    setForm(null); setEditing(null);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SPACING.lg }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary }}>Responsables & Validateurs <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>({personnes.length})</span></div>
        <button onClick={() => { setEditing(null); setForm({ ...emptyPersonne }); }}
          style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ Ajouter</button>
      </div>

      {form && (
        <FormModal
          title={editing ? "Modifier la personne" : "Nouvelle personne"}
          headerBackground="#1e3a5f"
          maxWidth={680}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={() => { setForm(null); setEditing(null); }} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Annuler</button>
              <button type="button" onClick={save} disabled={saving} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>{saving ? "Enregistrement..." : "Enregistrer"}</button>
            </div>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <FieldGroup label="Prénom"><Input value={form.prenom} onChange={set("prenom")} placeholder="Sophie" /></FieldGroup>
            <FieldGroup label="Nom"><Input value={form.nom} onChange={set("nom")} placeholder="Bernard" /></FieldGroup>
            <FieldGroup label="Niveau d'habilitation"><SelectField value={form.habilitation} onChange={set("habilitation")} options={HABILITATIONS} /></FieldGroup>
            <FieldGroup label="Fonction"><Input value={form.fonction} onChange={set("fonction")} placeholder="Ex : Déléguée à la Protection des Données" /></FieldGroup>
            <FieldGroup label="Email"><Input value={form.email} onChange={set("email")} placeholder="s.bernard@company.fr" /></FieldGroup>
            <FieldGroup label="Périmètre"><Input value={form.perimetre} onChange={set("perimetre")} placeholder="Ex : Conformité RGPD globale" /></FieldGroup>
          </div>
        </FormModal>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {personnes.map(p => {
          const c = habColor[p.habilitation] || habColor["Autre"];
          const isHighlighted = p.id === highlightId || Number(p.id) === highlightId;
          return (
            <div
              key={p.id}
              ref={isHighlighted ? highlightRef : null}
              style={{
                background: isHighlighted ? "#fffbeb" : COLORS.white,
                borderRadius: RADIUS.lg,
                border: `1.5px solid ${isHighlighted ? "#f59e0b" : COLORS.border}`,
                padding: 16,
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                transition: "box-shadow 0.15s",
                boxShadow: isHighlighted ? "0 0 0 2px #fef3c7" : "none",
              }}
              onMouseEnter={e => !isHighlighted && (e.currentTarget.style.boxShadow = "0 4px 12px rgba(37,99,235,0.08)")}
              onMouseLeave={e => !isHighlighted && (e.currentTarget.style.boxShadow = "none")}
            >
              <div style={{ width: 44, height: 44, borderRadius: "50%", background: c.bg, border: `2px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: c.text, flexShrink: 0 }}>
                {p.prenom[0]}{p.nom[0]}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: COLORS.textPrimary }}><HighlightText text={`${p.prenom} ${p.nom}`} term={searchTerm} /></span>
                  <HabBadge habilitation={p.habilitation} />
                </div>
                <div style={{ fontSize: 12, color: "#374151", fontWeight: 500, marginBottom: 3 }}>{p.fonction}</div>
                {p.email && <div style={{ fontSize: 11, color: COLORS.textSecondary }}>{p.email}</div>}
                {p.perimetre && <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 2 }}>◎ {p.perimetre}</div>}
              </div>
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                <button onClick={() => { setEditing(p); setForm({ ...p }); }} style={{ padding: `${SPACING.xs}px 9px`, borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.primaryAccent}`, background: "transparent", color: COLORS.primaryAccent, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdEdit size={14} /></button>
                <button
                  onClick={() => onDeletePersonne ? onDeletePersonne(p.id) : setPersonnes(ps => ps.filter(x => x.id !== p.id))}
                  style={{ padding: "4px 9px", borderRadius: RADIUS.sm, border: "1.5px solid #fee2e2", background: "#fff5f5", color: "#dc2626", fontSize: 12, cursor: "pointer" }}
                >
                  <MdDelete size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RefCategories({ categories, setCategories }) {
  const [form, setForm] = useState(null);

  const nextTempId = () => {
    let minNeg = null;
    for (const c of categories) {
      const id = Number(c.id);
      if (Number.isFinite(id) && id < 0) minNeg = minNeg === null ? id : Math.min(minNeg, id);
    }
    return minNeg === null ? -1 : minNeg - 1;
  };

  const deleteCategory = c => {
    if (isLockedOrganizationCategory(c)) {
      window.alert("Les catégories fournies par défaut ne peuvent pas être supprimées.");
      return;
    }
    if ((c.usageCount ?? 0) > 0) {
      window.alert("Impossible de supprimer cette catégorie : elle est encore utilisée par des preuves ou des activités.");
      return;
    }
    if (!window.confirm(`Supprimer la catégorie « ${c.label} » ?`)) return;
    setCategories(prev => prev.filter(x => String(x.id) !== String(c.id)));
  };

  const save = () => {
    if (!form) return;
    const desc = (form.description ?? "").trim();
    if (form.mode === "add") {
      const label = (form.label ?? "").trim();
      if (!label) {
        window.alert("Indiquez un libellé pour la nouvelle catégorie.");
        return;
      }
      if (categories.some(c => c.label === label)) {
        window.alert("Ce libellé existe déjà.");
        return;
      }
      setCategories(prev => [
        ...prev,
        {
          id: nextTempId(),
          label,
          description: desc,
          isSystemDefault: false,
          usageCount: 0,
        },
      ]);
    } else if (form.mode === "edit" && form.category) {
      const id = form.category.id;
      const isSystem = isLockedOrganizationCategory(form.category);
      const labelIn = (form.label ?? "").trim();
      const nextLabel = isSystem ? form.category.label : labelIn;
      if (!isSystem) {
        if (!labelIn) {
          window.alert("Le libellé ne peut pas être vide.");
          return;
        }
        if (categories.some(c => String(c.id) !== String(id) && c.label === labelIn)) {
          window.alert("Ce libellé existe déjà.");
          return;
        }
      }
      setCategories(prev =>
        prev.map(c =>
          String(c.id) !== String(id)
            ? c
            : { ...c, label: nextLabel, description: desc }
        )
      );
    }
    setForm(null);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SPACING.lg }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary }}>
            Catégories de preuves & activités RACI{" "}
            <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>({categories.length})</span>
          </div>
          <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 4, maxWidth: 780 }}>
            Huit catégories sont créées automatiquement à l&apos;ouverture du compte. Elles ne sont pas supprimables tant qu&apos;elles sont utilisées. Vous pouvez en ajouter d&apos;autres selon votre organisation ; elles servent à classer les preuves et les activités de la matrice RACI.
            {" "}
            <strong>Si vous supprimez votre dernière catégorie personnalisée</strong>, les huit catégories par défaut sont recréées côté serveur lorsque aucune liste ne subsiste.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setForm({ mode: "add", label: "", description: "" })}
          style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
        >
          + Catégorie personnalisée
        </button>
      </div>

      {form && (
        <FormModal
          title={form.mode === "add" ? "Nouvelle catégorie" : "Modifier la catégorie"}
          headerBackground="#1e3a5f"
          maxWidth={560}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={() => setForm(null)} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Annuler</button>
              <button type="button" onClick={save} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Enregistrer</button>
            </div>
          }
        >
          {form.mode === "edit" && form.category && isLockedOrganizationCategory(form.category) && (
            <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde047", borderRadius: RADIUS.md, padding: "10px 12px", marginBottom: 12 }}>
              Catégorie fournie par défaut : le libellé est fixe ; vous pouvez adapter la description.
            </div>
          )}
          <FieldGroup label="Libellé">
            <Input
              value={form.mode === "edit" && form.category && isLockedOrganizationCategory(form.category) ? form.category.label : (form.label ?? "")}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              placeholder="Ex. : Juridique interne"
              disabled={form.mode === "edit" && !!form.category && isLockedOrganizationCategory(form.category)}
            />
          </FieldGroup>
          <FieldGroup label="Description">
            <Textarea value={form.description ?? ""} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Précisez le périmètre de cette catégorie dans votre organisation…" />
          </FieldGroup>
        </FormModal>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
        {categories.map(c => {
          const usageBlocked = (c.usageCount ?? 0) > 0;
          const lockedDefault = isLockedOrganizationCategory(c);
          const color = c.color || "#475569";
          const bg = c.bg || "#f1f5f9";
          return (
          <div key={c.id} style={{ background: bg, borderRadius: RADIUS.lg, border: `1.5px solid ${color}40`, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                <span style={{ fontWeight: 700, fontSize: 13, color: color, wordBreak: "break-word" }}>{c.label}</span>
              </div>
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => setForm({ mode: "edit", category: c, label: c.label, description: c.description ?? "" })}
                  title="Modifier"
                  style={{ padding: `${SPACING.xs}px 9px`, borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.primaryAccent}`, background: "transparent", color: COLORS.primaryAccent, fontSize: 12, cursor: "pointer" }}
                >
                  <MdEdit size={14} />
                </button>
                {!lockedDefault && (
                  <button
                    type="button"
                    onClick={() => deleteCategory(c)}
                    title={usageBlocked ? "Cliquer pour voir pourquoi la suppression est bloquée" : "Supprimer"}
                    style={{
                      padding: "4px 9px",
                      borderRadius: RADIUS.sm,
                      border: `1.5px solid #fee2e2`,
                      background: usageBlocked ? "#f9fafb" : "#fff5f5",
                      color: usageBlocked ? COLORS.textMuted : "#dc2626",
                      fontSize: 12,
                      cursor: usageBlocked ? "help" : "pointer",
                      opacity: usageBlocked ? 0.85 : 1,
                    }}
                  >
                    <MdDelete size={14} />
                  </button>
                )}
              </div>
            </div>
            <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.5 }}>{c.description}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 10, fontWeight: 600 }}>
              {lockedDefault && (
                <span style={{ padding: "2px 8px", borderRadius: 99, background: COLORS.white, color: COLORS.textSecondary, border: `1px solid ${COLORS.border}` }}>Défaut</span>
              )}
              <span style={{ padding: "2px 8px", borderRadius: 99, background: COLORS.white, color: usageBlocked ? "#1d4ed8" : COLORS.textMuted, border: `1px solid ${COLORS.border}` }}>
                {usageBlocked ? `${c.usageCount} utilisation(s)` : "Non utilisée"}
              </span>
            </div>
          </div>
          );
        })}
      </div>

      <div style={{ marginTop: 14, padding: "10px 14px", background: "#f8fafc", borderRadius: RADIUS.md, border: `1px dashed ${COLORS.border}`, fontSize: 12, color: COLORS.textMuted, textAlign: "center" }}>
        Les changements sont enregistrés automatiquement sur le serveur (quelques secondes après la dernière modification).
      </div>
    </div>
  );
}

function RefDocTypes() {
  const [expanded, setExpanded] = useState(null);
  const { enabledByCode, setDocTypeEnabled, loading, error, sessionReadOnly } = useDocTypesEnabled();

  const toggleType = async (code, checked) => {
    try {
      const ok = await setDocTypeEnabled(code, checked);
      if (!ok && !checked) {
        window.alert("Au moins un type de document doit rester activé pour la création de preuve.");
      }
    } catch (e) {
      window.alert(e?.message || "Enregistrement impossible.");
    }
  };

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary, marginBottom: 4 }}>Types de documents RGPD</div>
      <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 8 }}>
        Chaque type qualifie une preuve pour l&apos;audit CNIL. Les définitions détaillées proviennent du référentiel Hapivot ; survolez le badge dans les listes pour le résumé.
      </div>
      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 14, padding: "8px 12px", background: "#f8fafc", borderRadius: RADIUS.md, border: `1px dashed ${COLORS.border}` }}>
        Activation pour le formulaire « Nouvelle preuve » : enregistrée <strong>côté serveur</strong> pour toute l&apos;organisation (partagée entre tous les utilisateurs et postes).
      </div>
      {error ? (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: RADIUS.md, background: "#fef2f2", border: `1px solid #fecaca`, fontSize: 12, color: "#991b1b" }}>
          {error}
        </div>
      ) : null}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {DOC_TYPES_WITH_REF.map(dt => {
          const r = dt.ref;
          const enabled = enabledByCode[dt.code] !== false;
          const open = expanded === dt.code;
          return (
            <div key={dt.code} style={{ background: COLORS.white, borderRadius: RADIUS.lg, border: `1.5px solid ${dt.border}`, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ background: dt.bg, border: `1.5px solid ${dt.border}`, borderRadius: RADIUS.md, padding: "8px 12px", flexShrink: 0, minWidth: 60, textAlign: "center" }}>
                  <span style={{ fontWeight: 900, fontSize: 14, color: dt.color }}>{dt.code}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: COLORS.textPrimary, marginBottom: 5 }}>{dt.label}</div>
                  <div style={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.65 }}>{dt.longDesc}</div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, cursor: sessionReadOnly || loading ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600, color: COLORS.textPrimary, opacity: sessionReadOnly || loading ? 0.65 : 1 }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      disabled={sessionReadOnly || loading}
                      onChange={e => toggleType(dt.code, e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: COLORS.primaryAccent, cursor: sessionReadOnly || loading ? "not-allowed" : "pointer" }}
                    />
                    Proposer dans « Nouvelle preuve »
                  </label>
                </div>
              </div>
              {r && (
                <>
                  <button
                    type="button"
                    onClick={() => setExpanded(open ? null : dt.code)}
                    style={{
                      alignSelf: "flex-start",
                      padding: "4px 10px",
                      borderRadius: RADIUS.sm,
                      border: `1px solid ${COLORS.border}`,
                      background: "#f8fafc",
                      fontSize: 11,
                      fontWeight: 700,
                      color: COLORS.primaryAccent,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {open ? "▼ Masquer les détails référentiel" : "▸ Déclencheur, exemples, révision…"}
                  </button>
                  {open && (
                    <div style={{ fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.65, paddingTop: 4, borderTop: `1px solid ${COLORS.border}` }}>
                      {r.article_rgpd && (
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, color: COLORS.textPrimary }}>Cadre RGPD : </span>
                          {r.article_rgpd}
                          {r.statut ? ` · ${r.statut}` : ""}
                        </div>
                      )}
                      {r.declencheur && (
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, color: COLORS.textPrimary }}>Déclencheur : </span>
                          {r.declencheur}
                        </div>
                      )}
                      {Array.isArray(r.exemples) && r.exemples.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontWeight: 700, color: COLORS.textPrimary, marginBottom: 4 }}>Exemples</div>
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {r.exemples.map((ex, i) => (
                              <li key={i}>{ex}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {Array.isArray(r.producteurs) && r.producteurs.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, color: COLORS.textPrimary }}>Producteurs habituels : </span>
                          {r.producteurs.join(", ")}
                        </div>
                      )}
                      {r.revision && (
                        <div>
                          <span style={{ fontWeight: 700, color: COLORS.textPrimary }}>Révision : </span>
                          {r.revision}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function RefActivites({ activites, setActivites, personnes, categories, searchHighlight }) {
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const highlightRef = useRef(null);

  const makeEmptyActivite = () => ({
    intitule: "",
    categorie: categories?.[0]?.label ?? DEFAULT_FIRST_ORGANIZATION_CATEGORY_LABEL,
    description: "",
    responsableId: "",
    docTypes: [],
    periodicite: "",
    echeance: "",
  });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const highlightId = searchHighlight?.id != null ? Number(searchHighlight.id) : null;
  const searchTerm = searchHighlight?.searchTerm || null;

  useEffect(() => {
    if (highlightId != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId]);

  const toggleDocType = code => {
    setForm(f => ({
      ...f,
      docTypes: f.docTypes.includes(code) ? f.docTypes.filter(d => d !== code) : [...f.docTypes, code],
    }));
  };

  const save = () => {
    const nextId = activites.reduce((max, item) => Math.max(max, Number(item.id) || 0), 0) + 1;
    if (editing) setActivites(as => as.map(a => a.id === editing.id ? { ...form, id: editing.id } : a));
    else setActivites(as => [...as, { ...form, id: nextId }]);
    setForm(null); setEditing(null);
  };

  const personneOptions = [
    { value: "", label: "— Sélectionner —" },
    ...personnes.map(p => ({ value: p.id, label: `${p.prenom} ${p.nom} (${p.habilitation})` })),
  ];

  const catColors = categoryBadgeStylesFromCategories(categories);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SPACING.lg }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary }}>Activités RGPD <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>({activites.length})</span></div>
          <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 2 }}>Ces activités alimenteront la matrice RACI. Chacune référence des responsables et des types de documents.</div>
        </div>
        <button onClick={() => { setEditing(null); setForm(makeEmptyActivite()); }}
          style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ Ajouter</button>
      </div>

      {form && (
        <FormModal
          title={editing ? "Modifier l'activité" : "Nouvelle activité RGPD"}
          headerBackground="#1e3a5f"
          maxWidth={760}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={() => { setForm(null); setEditing(null); }} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Annuler</button>
              <button type="button" onClick={save} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Enregistrer</button>
            </div>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Intitulé de l'activité"><Input value={form.intitule} onChange={set("intitule")} placeholder="Ex : Rédaction et signature DPA OVH" /></FieldGroup>
            </div>
            <FieldGroup label="Catégorie">
              <select value={form.categorie} onChange={set("categorie")}
                style={{ width: "100%", padding: "7px 10px", borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.border}`, fontSize: 13, color: COLORS.textPrimary, background: COLORS.white, outline: "none", fontFamily: "inherit", boxSizing: "border-box", cursor: "pointer" }}>
                {(categories || []).map(c => (
                  <option key={c.label} value={c.label}>{c.label}</option>
                ))}
              </select>
            </FieldGroup>
            <FieldGroup label="Responsable principal">
              <SelectField value={form.responsableId} onChange={set("responsableId")} options={personneOptions} />
            </FieldGroup>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Description"><Textarea value={form.description} onChange={set("description")} placeholder="Décrivez l'activité, son périmètre et ses enjeux RGPD…" /></FieldGroup>
            </div>
            <FieldGroup label="Périodicité"><Input value={form.periodicite} onChange={set("periodicite")} placeholder="Ex : Annuelle, Semestrielle, Ponctuelle…" /></FieldGroup>
            <FieldGroup label="Échéance"><Input type="date" value={form.echeance} onChange={set("echeance")} /></FieldGroup>
            <div />
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Types de documents concernés (multi-sélection)">
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                  {DOC_TYPES.map(dt => {
                    const selected = form.docTypes.includes(dt.code);
                    return (
                      <button key={dt.code} type="button" onClick={() => toggleDocType(dt.code)} style={{ padding: "4px 12px", borderRadius: 99, fontSize: 11, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", background: selected ? dt.bg : "#f9fafb", color: selected ? dt.color : "#9ca3af", border: `1.5px solid ${selected ? dt.border : COLORS.border}` }}>
                        {dt.label}
                      </button>
                    );
                  })}
                </div>
              </FieldGroup>
            </div>
          </div>
        </FormModal>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {activites.map(a => {
          const cat = catColors[a.categorie] || { color: "#374151", bg: "#f9fafb" };
          const resp = personnes.find(p => p.id === a.responsableId || p.id === Number(a.responsableId));
          const hc = resp ? (habColor[resp.habilitation] || habColor["Autre"]) : null;
          const isLate = a.echeance && new Date(a.echeance) < new Date();
          const isHighlighted = a.id === highlightId || Number(a.id) === highlightId;
          return (
            <div
              key={a.id}
              ref={isHighlighted ? highlightRef : null}
              style={{
                background: isHighlighted ? "#fffbeb" : COLORS.white,
                borderRadius: RADIUS.lg,
                border: `1.5px solid ${isHighlighted ? "#f59e0b" : COLORS.border}`,
                padding: 16,
                display: "flex",
                gap: 14,
                alignItems: "flex-start",
                transition: "box-shadow 0.15s",
                boxShadow: isHighlighted ? "0 0 0 2px #fef3c7" : "none",
              }}
              onMouseEnter={e => !isHighlighted && (e.currentTarget.style.boxShadow = "0 4px 12px rgba(37,99,235,0.08)")}
              onMouseLeave={e => !isHighlighted && (e.currentTarget.style.boxShadow = "none")}
            >
              <div style={{ width: 4, borderRadius: 99, background: cat.color, alignSelf: "stretch", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: COLORS.textPrimary, marginBottom: 4 }}><HighlightText text={a.intitule} term={searchTerm} /></div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 99, background: cat.bg, color: cat.color, border: `1px solid ${cat.color}30` }}>{a.categorie}</span>
                      {a.docTypes && a.docTypes.map(code => <DocTypeBadge key={code} code={code} />)}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <button onClick={() => { setEditing(a); setForm({ ...a }); }} style={{ padding: `${SPACING.xs}px 9px`, borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.primaryAccent}`, background: "transparent", color: COLORS.primaryAccent, fontSize: 12, cursor: "pointer" }}>✏</button>
                    <button onClick={() => setActivites(as => as.filter(x => x.id !== a.id))} style={{ padding: "4px 9px", borderRadius: RADIUS.sm, border: "1.5px solid #fee2e2", background: "#fff5f5", color: "#dc2626", fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdDelete size={14} /></button>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 8, lineHeight: 1.5 }}><HighlightText text={a.description} term={searchTerm} /></div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", fontSize: 11 }}>
                  {resp && hc && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", background: hc.bg, border: `1.5px solid ${hc.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: hc.text, flexShrink: 0 }}>
                        {resp.prenom[0]}{resp.nom[0]}
                      </div>
                      <span style={{ color: "#374151" }}>{resp.prenom} {resp.nom}</span>
                      <HabBadge habilitation={resp.habilitation} />
                    </div>
                  )}
                  {a.periodicite && <span style={{ color: COLORS.textSecondary, display: "inline-flex", alignItems: "center", gap: 4 }}><MdSync size={13} /> {a.periodicite}</span>}
                  {a.echeance && (
                    <span style={{ color: isLate ? "#dc2626" : "#6b7280", fontWeight: isLate ? 700 : 400 }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>{isLate ? <MdWarningAmber size={13} /> : <MdCalendarToday size={13} />}{isLate ? "Échéance dépassée :" : ""} {formatDateFr(a.echeance)}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RefActivitesCatalog({ activites, setActivites, personnes, categories, searchHighlight, setCategories }) {
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [newCategoryName, setNewCategoryName] = useState("");
  const highlightRef = useRef(null);
  const highlightId = searchHighlight?.id != null ? Number(searchHighlight.id) : null;
  const searchTerm = searchHighlight?.searchTerm || null;
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const makeEmptyActivite = (forcedCategory) => ({
    intitule: "",
    categorie: forcedCategory || categories?.[0]?.label || DEFAULT_FIRST_ORGANIZATION_CATEGORY_LABEL,
    description: "",
    responsableId: "",
    docTypes: [],
    periodicite: "",
    echeance: "",
  });

  const toggleDocType = code => {
    setForm(f => ({
      ...f,
      docTypes: f.docTypes.includes(code) ? f.docTypes.filter(d => d !== code) : [...f.docTypes, code],
    }));
  };

  const saveActivity = () => {
    const nextId = activites.reduce((max, item) => Math.max(max, Number(item.id) || 0), 0) + 1;
    if (editing) setActivites(as => as.map(a => a.id === editing.id ? { ...form, id: editing.id } : a));
    else setActivites(as => [...as, { ...form, id: nextId, enabled: true, isDefault: false }]);
    setForm(null);
    setEditing(null);
  };

  const toggleActivityEnabled = (activityId) => {
    setActivites(list =>
      list.map(item =>
        String(item.id) === String(activityId)
          ? { ...item, enabled: item.enabled === false ? true : false }
          : item
      )
    );
  };

  const nextTempCategoryId = () => {
    let minNeg = null;
    for (const c of categories) {
      const id = Number(c.id);
      if (Number.isFinite(id) && id < 0) minNeg = minNeg === null ? id : Math.min(minNeg, id);
    }
    return minNeg === null ? -1 : minNeg - 1;
  };

  const addCustomCategory = () => {
    const label = newCategoryName.trim();
    if (!label) return;
    if ((categories || []).some(c => c.label.toLowerCase() === label.toLowerCase())) {
      window.alert("Cette catégorie existe déjà.");
      return;
    }
    setCategories(prev => [...prev, { id: nextTempCategoryId(), label, description: "", isSystemDefault: false, usageCount: 0 }]);
    setExpanded(prev => ({ ...prev, [label]: true }));
    setNewCategoryName("");
  };

  const personneOptions = [
    { value: "", label: "— Sélectionner —" },
    ...personnes.map(p => ({ value: p.id, label: `${p.prenom} ${p.nom} (${p.habilitation})` })),
  ];

  const grouped = (categories || []).map(c => ({
    category: c,
    items: activites.filter(a => a.categorie === c.label),
    isDefault: isLockedOrganizationCategory(c),
  }));

  const stats = {
    categories: categories.length,
    defaultActivities: activites.filter(a => a.isDefault === true && a.enabled !== false).length,
    customCategories: grouped.filter(g => !g.isDefault).length,
  };

  useEffect(() => {
    if (highlightId == null) return;
    const target = activites.find(a => a.id === highlightId || Number(a.id) === highlightId);
    if (target?.categorie) {
      setExpanded(prev => ({ ...prev, [target.categorie]: true }));
    }
  }, [highlightId, activites]);

  useEffect(() => {
    if (highlightId != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId]);

  const renderCategoryBlock = ({ category, items, isDefault }) => {
    const color = category.color || (isDefault ? "#475569" : "#65a30d");
          const isOpen = expanded[category.label] ?? false;
    return (
      <div key={category.label} style={{ border: `1px ${isDefault ? "solid" : "dashed"} ${COLORS.border}`, borderRadius: RADIUS.md, background: isDefault ? "#fff" : "#fcfcfd" }}>
        <div style={{ padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 11, height: 11, borderRadius: "50%", background: color }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: COLORS.textPrimary }}>{category.label}</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button type="button" onClick={() => { setEditing(null); setForm(makeEmptyActivite(category.label)); }} style={{ padding: "4px 10px", borderRadius: RADIUS.sm, border: `1px solid ${COLORS.border}`, background: "#f8fafc", color: COLORS.primaryAccent, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>+ Activité</button>
            <span style={{ fontSize: 12, color: COLORS.textSecondary, fontWeight: 700 }}>{items.filter(a => a.enabled !== false).length} activité{items.filter(a => a.enabled !== false).length > 1 ? "s" : ""}</span>
            <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99, border: `1px solid ${COLORS.border}`, background: isDefault ? "#f8fafc" : "#f5f3ff", color: isDefault ? COLORS.textSecondary : "#7c3aed" }}>
              {isDefault ? "Par défaut" : "Personnalisée"}
            </span>
            <button type="button" onClick={() => setExpanded(prev => ({ ...prev, [category.label]: !isOpen }))} style={{ border: "none", background: "transparent", cursor: "pointer", color: COLORS.textMuted, fontSize: 16 }}>{isOpen ? "▾" : "▸"}</button>
          </div>
        </div>
        {isOpen && (
          <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: "8px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
            {items.length === 0 ? <div style={{ fontSize: 12, color: COLORS.textMuted }}>Aucune activité.</div> : items.map(a => {
              const isHighlighted = a.id === highlightId || Number(a.id) === highlightId;
              const isDefaultActivity = a.isDefault === true;
              const isEnabled = a.enabled !== false;
              return (
                <div key={a.id} ref={isHighlighted ? highlightRef : null} style={{ border: `1px solid ${isHighlighted ? "#f59e0b" : COLORS.border}`, borderRadius: RADIUS.sm, background: isHighlighted ? "#fffbeb" : "#fff", padding: "8px 10px", display: "flex", justifyContent: "space-between", gap: 8, opacity: isEnabled ? 1 : 0.6 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary }}><HighlightText text={a.intitule} term={searchTerm} /></div>
                    {a.description ? <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 2 }}><HighlightText text={a.description} term={searchTerm} /></div> : null}
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {isDefaultActivity ? (
                      <button type="button" onClick={() => toggleActivityEnabled(a.id)} style={{ padding: "4px 9px", borderRadius: RADIUS.sm, border: `1.5px solid ${isEnabled ? "#f59e0b" : "#86efac"}`, background: isEnabled ? "#fffbeb" : "#f0fdf4", color: isEnabled ? "#92400e" : "#166534", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                        {isEnabled ? "Désactiver" : "Activer"}
                      </button>
                    ) : (
                      <>
                        <button type="button" onClick={() => { setEditing(a); setForm({ ...a }); }} style={{ padding: "4px 9px", borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.primaryAccent}`, background: "transparent", color: COLORS.primaryAccent, cursor: "pointer" }}><MdEdit size={14} /></button>
                        <button type="button" onClick={() => setActivites(as => as.filter(x => x.id !== a.id))} style={{ padding: "4px 9px", borderRadius: RADIUS.sm, border: "1.5px solid #fee2e2", background: "#fff5f5", color: "#dc2626", cursor: "pointer" }}><MdDelete size={14} /></button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary, marginBottom: 2 }}>Catégories & Activités</div>
      <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 10 }}>Référentiel de classification des preuves RGPD et de la Matrice RACI</div>
      <div style={{ background: "#eff6ff", border: "1.5px solid #93c5fd", borderRadius: RADIUS.md, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#1e40af", fontWeight: 600 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><MdInfo size={16} /> Les éléments par défaut couvrent les grands domaines RGPD. Vous pouvez ajouter des activités dans une catégorie existante, ou créer une nouvelle catégorie avec ses propres activités.</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 10, marginBottom: 14 }}>
        {[{ l: "Catégories", v: stats.categories }, { l: "Activités par défaut", v: stats.defaultActivities }, { l: "Personnalisée", v: stats.customCategories }].map(x => (
          <div key={x.l} style={{ background: "#f8fafc", border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "10px 14px" }}>
            <div style={{ fontSize: 32, lineHeight: 1, fontWeight: 800, color: COLORS.textPrimary }}>{x.v}</div>
            <div style={{ fontSize: 12, color: COLORS.textSecondary, fontWeight: 600, marginTop: 2 }}>{x.l}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 12, fontWeight: 800, color: COLORS.textSecondary, marginBottom: 8 }}>CATÉGORIES PAR DÉFAUT</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        {grouped.filter(g => g.isDefault).map(renderCategoryBlock)}
      </div>

      <div style={{ fontSize: 12, fontWeight: 800, color: COLORS.textSecondary, marginBottom: 8 }}>CATÉGORIES PERSONNALISÉES</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
        {grouped.filter(g => !g.isDefault).length === 0 ? (
          <div style={{ border: `1px dashed ${COLORS.border}`, borderRadius: RADIUS.md, padding: "10px 12px", fontSize: 12, color: COLORS.textMuted }}>Aucune catégorie personnalisée.</div>
        ) : grouped.filter(g => !g.isDefault).map(renderCategoryBlock)}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
        <Input value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} placeholder="Nom de la nouvelle catégorie..." />
        <button type="button" onClick={addCustomCategory} style={{ padding: "8px 14px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ Nouvelle catégorie</button>
      </div>

      {form && (
        <FormModal
          title={editing ? "Modifier l'activité" : "Nouvelle activité RGPD"}
          headerBackground="#1e3a5f"
          maxWidth={760}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={() => { setForm(null); setEditing(null); }} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Annuler</button>
              <button type="button" onClick={saveActivity} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Enregistrer</button>
            </div>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Intitulé de l'activité"><Input value={form.intitule} onChange={set("intitule")} placeholder="Ex : Rédaction et signature des DPA (art. 28)" /></FieldGroup>
            </div>
            <FieldGroup label="Catégorie">
              <select value={form.categorie} onChange={set("categorie")}
                style={{ width: "100%", padding: "7px 10px", borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.border}`, fontSize: 13, color: COLORS.textPrimary, background: COLORS.white, outline: "none", fontFamily: "inherit", boxSizing: "border-box", cursor: "pointer" }}>
                {(categories || []).map(c => (
                  <option key={c.label} value={c.label}>{c.label}</option>
                ))}
              </select>
            </FieldGroup>
            <FieldGroup label="Responsable principal">
              <SelectField value={form.responsableId} onChange={set("responsableId")} options={personneOptions} />
            </FieldGroup>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Description"><Textarea value={form.description} onChange={set("description")} placeholder="Décrivez l'activité, son périmètre et ses enjeux RGPD…" /></FieldGroup>
            </div>
            <FieldGroup label="Périodicité"><Input value={form.periodicite} onChange={set("periodicite")} placeholder="Ex : Annuelle, Semestrielle, Ponctuelle…" /></FieldGroup>
            <FieldGroup label="Échéance"><Input type="date" value={form.echeance} onChange={set("echeance")} /></FieldGroup>
            <div />
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Types de documents concernés (multi-sélection)">
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                  {DOC_TYPES.map(dt => {
                    const selected = form.docTypes.includes(dt.code);
                    return (
                      <button key={dt.code} type="button" onClick={() => toggleDocType(dt.code)} style={{ padding: "4px 12px", borderRadius: 99, fontSize: 11, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", background: selected ? dt.bg : "#f9fafb", color: selected ? dt.color : "#9ca3af", border: `1.5px solid ${selected ? dt.border : COLORS.border}` }}>
                        {dt.label}
                      </button>
                    );
                  })}
                </div>
              </FieldGroup>
            </div>
          </div>
        </FormModal>
      )}
    </div>
  );
}

// ─── MATRICE RACI ─────────────────────────────────────────────────────────────

const RACI_CYCLE = ["R", "A", "C", "I", ""];
const RACI_META = {
  R: { label: "R", full: "Responsible",  desc: "Réalise l'activité",              bg: "#dbeafe", text: "#1d4ed8", border: "#93c5fd" },
  A: { label: "A", full: "Accountable",  desc: "Rend des comptes (unique)",       bg: "#fef9c3", text: "#92400e", border: "#fde047" },
  C: { label: "C", full: "Consulted",    desc: "Consulté avant la décision",      bg: "#dcfce7", text: "#15803d", border: "#86efac" },
  I: { label: "I", full: "Informed",     desc: "Informé après la décision",       bg: "#f3e8ff", text: "#7e22ce", border: "#d8b4fe" },
  "": { label: "—", full: "",            desc: "",                                bg: "#f9fafb", text: "#d1d5db", border: "#f3f4f6" },
};

function RaciLegend() {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
      {["R","A","C","I"].map(k => {
        const m = RACI_META[k];
        return (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 12px", borderRadius: RADIUS.md, background: m.bg, border: `1.5px solid ${m.border}` }}>
            <span style={{ fontWeight: 900, fontSize: 13, color: m.text, minWidth: 12 }}>{k}</span>
            <span style={{ fontSize: 11, color: m.text, fontWeight: 600 }}>{m.full}</span>
            <span style={{ fontSize: 11, color: m.text, opacity: 0.75 }}>— {m.desc}</span>
          </div>
        );
      })}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: RADIUS.md, background: "#f1f5f9", border: `1.5px solid ${COLORS.border}` }}>
        <span style={{ fontSize: 11, color: COLORS.textSecondary, display: "inline-flex", alignItems: "center", gap: 4 }}><MdLightbulb size={13} /> Cliquez sur une cellule pour cycler R → A → C → I → vide</span>
      </div>
    </div>
  );
}

function RaciMatrix({ activites, personnes, matrix, setMatrix, categories }) {
  const matrixWithDefaults = (() => {
    const fallback = { ...(matrix || {}) };
    activites.forEach(a => {
      const actKey = String(a.id);
      const row = { ...(fallback[actKey] || {}) };
      const hasAnyRole = Object.values(row).some(v => ["R", "A", "C", "I"].includes(v));
      if (!hasAnyRole && a.responsableId !== "" && a.responsableId !== null && a.responsableId !== undefined) {
        row[String(a.responsableId)] = "A";
      }
      fallback[actKey] = row;
    });
    return fallback;
  })();

  const cycleCell = (actId, perId) => {
    setMatrix(m => {
      const actKey = String(actId);
      const perKey = String(perId);
      const current = m?.[actKey]?.[perKey] || "";
      const idx = RACI_CYCLE.indexOf(current);
      const row = { ...(m?.[actKey] || {}) };
      const alreadyHasAElsewhere = personnes.some(
        p => p.id !== perId && row[String(p.id)] === "A"
      );

      // Si la ligne a déjà un A ailleurs, on saute simplement cette étape du cycle
      // (R -> C au lieu de bloquer sur R).
      let offset = 1;
      let next = RACI_CYCLE[(idx + offset) % RACI_CYCLE.length];
      while (next === "A" && alreadyHasAElsewhere) {
        offset += 1;
        next = RACI_CYCLE[(idx + offset) % RACI_CYCLE.length];
      }

      return { ...m, [actKey]: { ...row, [perKey]: next } };
    });
  };

  const getCell = (actId, perId) => matrixWithDefaults[String(actId)]?.[String(perId)] || "";

  const activitesNoA = activites.filter(a =>
    !personnes.some(p => matrixWithDefaults[String(a.id)]?.[String(p.id)] === "A")
  );

  const catColors = categoryBadgeStylesFromCategories(categories);
  const groupedActivities = (categories || []).map(c => ({
    label: c.label,
    color: (catColors[c.label]?.color) || "#334155",
    bg: (catColors[c.label]?.bg) || "#f8fafc",
    items: activites.filter(a => a.categorie === c.label),
  }));
  const uncategorized = activites.filter(a => !groupedActivities.some(g => g.label === a.categorie));
  if (uncategorized.length > 0) {
    groupedActivities.push({
      label: "Autres activités",
      color: "#334155",
      bg: "#f8fafc",
      items: uncategorized,
    });
  }

  if (activites.length === 0 || personnes.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "48px 0", color: COLORS.textMuted, fontSize: 14 }}>
        Ajoutez des <strong>activités</strong> (Configuration → Référentiels → Activités) et des <strong>responsables</strong> (idem → Responsables) pour éditer la matrice.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary }}>Matrice RACI RGPD</div>
          <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 2 }}>
            {activites.length} activités × {personnes.length} intervenants — Un seul <strong style={{ color: "#92400e" }}>A</strong> (Accountable) autorisé par ligne.
          </div>
        </div>
        {activitesNoA.length > 0 && (
          <div style={{ background: "#fef3c7", border: "1.5px solid #fde047", borderRadius: RADIUS.md, padding: "6px 12px", fontSize: 11, color: "#92400e", fontWeight: 600 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><MdWarningAmber size={13} /> {activitesNoA.length} activité{activitesNoA.length > 1 ? "s" : ""} sans Accountable</span>
          </div>
        )}
      </div>

      <RaciLegend />

      <div style={{ overflowX: "auto", borderRadius: RADIUS.lg, border: `1.5px solid ${COLORS.border}` }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 700 }}>
          <thead>
            <tr style={{ background: COLORS.primary }}>
              <th style={{ padding: `${SPACING.md}px ${SPACING.lg}px`, textAlign: "left", fontSize: 11, fontWeight: 700, color: COLORS.primarySoft, letterSpacing: "0.06em", textTransform: "uppercase", minWidth: 220, position: "sticky", left: 0, background: COLORS.primary, zIndex: 2, borderRight: `2px solid ${COLORS.primaryAccent}` }}>
                Activité RGPD
              </th>
              {personnes.map(p => {
                const hc = habColor[p.habilitation] || habColor["Autre"];
                return (
                  <th key={p.id} style={{ padding: "8px 12px", textAlign: "center", minWidth: 90, borderLeft: "1px solid #2d4a7a" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <div style={{ width: 30, height: 30, borderRadius: "50%", background: hc.bg, border: `2px solid ${hc.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: hc.text }}>
                        {p.prenom[0]}{p.nom[0]}
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.white, lineHeight: 1.2, textAlign: "center" }}>{p.prenom}<br/>{p.nom}</div>
                      <div style={{ fontSize: 9, color: "#93c5fd", fontWeight: 600 }}>{p.habilitation}</div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {groupedActivities.filter(g => g.items.length > 0).flatMap((group) => [
                <tr key={`cat-${group.label}`} style={{ background: group.bg }}>
                  <td
                    colSpan={personnes.length + 1}
                    style={{
                      padding: "8px 12px",
                      borderBottom: `1px solid ${COLORS.border}`,
                      borderTop: `1px solid ${COLORS.border}`,
                      fontSize: 12,
                      fontWeight: 800,
                      color: group.color,
                    }}
                  >
                    {group.label}
                  </td>
                </tr>,
                ...group.items.map((a, ai) => {
                  const rowBg = ai % 2 === 0 ? "#fff" : "#f8fafc";
                  return (
                    <tr key={`act-${group.label}-${a.id}`} style={{ background: rowBg }}>
                      <td style={{ padding: "10px 16px", borderRight: `2px solid ${COLORS.border}`, borderBottom: "1px solid #f1f5f9", position: "sticky", left: 0, background: rowBg, zIndex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textPrimary }}>{a.intitule}</div>
                      </td>
                      {personnes.map(p => {
                        const val = getCell(a.id, p.id);
                        const m = RACI_META[val] || RACI_META[""];
                        return (
                          <td key={p.id} style={{ textAlign: "center", borderLeft: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", padding: 6 }}>
                            <button
                              onClick={() => cycleCell(a.id, p.id)}
                              title={val ? `${m.full} — ${m.desc}\nCliquer pour changer` : "Cliquer pour assigner un rôle"}
                              style={{
                                width: 36, height: 36, borderRadius: RADIUS.md, border: `1.5px solid ${m.border}`,
                                background: m.bg, color: m.text, fontWeight: 900, fontSize: 13,
                                cursor: "pointer", transition: "all 0.1s", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto",
                              }}
                              onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.15)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.12)"; }}
                              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "none"; }}
                            >
                              {val || "·"}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                }),
              ])}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
        {personnes.map(p => {
          const hc = habColor[p.habilitation] || habColor["Autre"];
          const counts = { R: 0, A: 0, C: 0, I: 0 };
          activites.forEach(a => {
            const v = getCell(a.id, p.id);
            if (counts[v] !== undefined) counts[v]++;
          });
          return (
            <div key={p.id} style={{ background: COLORS.white, borderRadius: RADIUS.lg, border: `1.5px solid ${COLORS.border}`, padding: "10px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
                <div style={{ width: 26, height: 26, borderRadius: "50%", background: hc.bg, border: `1.5px solid ${hc.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: hc.text, flexShrink: 0 }}>
                  {p.prenom[0]}{p.nom[0]}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textPrimary, lineHeight: 1.2 }}>{p.prenom} {p.nom}</div>
              </div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {["R","A","C","I"].map(k => {
                  const m = RACI_META[k];
                  return counts[k] > 0 ? (
                    <span key={k} style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99, background: m.bg, color: m.text, border: `1px solid ${m.border}` }}>
                      {k} ×{counts[k]}
                    </span>
                  ) : null;
                })}
                {Object.values(counts).every(v => v === 0) && <span style={{ fontSize: 10, color: "#d1d5db" }}>Aucun rôle</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── REF SOUS-TRAITANTS ───────────────────────────────────────────────────────

const emptyST = { nom: "", pays: "France", horsEEE: false, typeService: "", donneestraites: "", baseLegale: "Contrat (art. 6(1)(b))", niveauRisque: "Modéré", dpaStatut: "À signer", dpaDate: "", dpaEcheance: "", responsableId: "", sccRequis: false, sccStatut: "", tiaStatut: "", commentaires: "", preuveRef: "" };

const BASE_LEGALES = ["Contrat (art. 6(1)(b))", "Consentement (art. 6(1)(a))", "Intérêt légitime (art. 6(1)(f))", "Obligation légale (art. 6(1)(c))", "Mission d'intérêt public (art. 6(1)(e))"];
const DPA_STATUTS = ["Signé", "À signer", "En cours de négociation", "Expiré", "Non requis"];
const SCC_STATUTS = ["Activées (DPA)", "Activées (avenant)", "Non activées", "En cours", "Non requises"];
const TIA_STATUTS = ["Réalisé", "À réaliser", "En cours", "Non requis"];

function RefSousTraitants({ sousTraitants, setSousTraitants, personnes, proofs, searchHighlight }) {
  const [form, setForm] = useState(null);
  const highlightRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [filterRisque, setFilterRisque] = useState("Tous");
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setCheck = k => e => setForm(f => ({ ...f, [k]: e.target.checked }));

  const save = () => {
    const nextId = sousTraitants.reduce((max, item) => Math.max(max, Number(item.id) || 0), 0) + 1;
    if (editing) setSousTraitants(ss => ss.map(s => s.id === editing.id ? { ...form, id: editing.id } : s));
    else setSousTraitants(ss => [...ss, { ...form, id: nextId }]);
    setForm(null); setEditing(null);
  };

  const personneOptions = [
    { value: "", label: "— Sélectionner —" },
    ...personnes.map(p => ({ value: p.id, label: `${p.prenom} ${p.nom} (${p.habilitation})` })),
  ];
  const preuveOptions = [
    { value: "", label: "— Aucune preuve liée —" },
    ...proofs.map(p => ({ value: p.intitule, label: p.intitule })),
  ];

  const filtered = filterRisque === "Tous" ? sousTraitants : sousTraitants.filter(s => s.niveauRisque === filterRisque);
  const highlightId = searchHighlight?.id != null ? Number(searchHighlight.id) : null;
  const searchTerm = searchHighlight?.searchTerm || null;

  useEffect(() => {
    if (highlightId != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId]);

  const nbHorsEEE = sousTraitants.filter(s => s.horsEEE).length;
  const nbDpaKo = sousTraitants.filter(s => s.dpaStatut !== "Signé" && s.dpaStatut !== "Non requis").length;
  const nbSccKo = sousTraitants.filter(s => s.sccRequis && s.sccStatut !== "Activées (DPA)" && s.sccStatut !== "Activées (avenant)" && s.sccStatut !== "Non requises").length;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary }}>Sous-traitants <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>({sousTraitants.length})</span></div>
          <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 2 }}>Référentiel des prestataires traitant des données personnelles pour votre compte (art. 28 RGPD).</div>
        </div>
        <button onClick={() => { setEditing(null); setForm({ ...emptyST }); }}
          style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ Ajouter</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
        {[
          { label: "Total", val: sousTraitants.length, color: "#1e3a5f", bg: "#eff6ff", border: "#bfdbfe" },
          { label: "Hors EEE", val: nbHorsEEE, color: "#991b1b", bg: "#fee2e2", border: "#fca5a5" },
          { label: "DPA manquants", val: nbDpaKo, color: "#92400e", bg: "#fef3c7", border: "#fcd34d" },
          { label: "CCT non activées", val: nbSccKo, color: "#9d174d", bg: "#fdf2f8", border: "#f9a8d4" },
        ].map(k => (
          <div key={k.label} style={{ background: k.bg, borderRadius: RADIUS.md, padding: "10px 14px", border: `1.5px solid ${k.border}` }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.val}</div>
            <div style={{ fontSize: 11, color: k.color, marginTop: 2, fontWeight: 600 }}>{k.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, alignItems: "center" }}>
        <span style={{ fontSize: 11, color: COLORS.textSecondary, fontWeight: 600 }}>Risque :</span>
        {["Tous", ...RISQUE_ST].map(r => (
          <button key={r} onClick={() => setFilterRisque(r)} style={{ padding: "3px 12px", borderRadius: 99, fontSize: 11, fontWeight: 600, cursor: "pointer", background: filterRisque === r ? COLORS.primary : COLORS.white, color: filterRisque === r ? COLORS.white : COLORS.textPrimary, border: `1.5px solid ${filterRisque === r ? COLORS.primary : COLORS.border}` }}>{r}</button>
        ))}
      </div>

      {form && (
        <FormModal
          title={editing ? "Modifier le sous-traitant" : "Nouveau sous-traitant"}
          headerBackground="#1e3a5f"
          maxWidth={820}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={() => { setForm(null); setEditing(null); }} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Annuler</button>
              <button type="button" onClick={save} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Enregistrer</button>
            </div>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Nom du sous-traitant"><Input value={form.nom} onChange={set("nom")} placeholder="Ex : OVH SAS, Mailchimp / Intuit Inc." /></FieldGroup>
            </div>
            <FieldGroup label="Pays d'établissement"><SelectField value={form.pays} onChange={set("pays")} options={PAYS_ADEQUATION} /></FieldGroup>
            <FieldGroup label="Niveau de risque"><SelectField value={form.niveauRisque} onChange={set("niveauRisque")} options={RISQUE_ST} /></FieldGroup>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Type de service"><Input value={form.typeService} onChange={set("typeService")} placeholder="Ex : Hébergement cloud, Emailing, Paiement…" /></FieldGroup>
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Données traitées"><Input value={form.donneestraites} onChange={set("donneestraites")} placeholder="Ex : Emails clients, données bancaires tokenisées…" /></FieldGroup>
            </div>
            <FieldGroup label="Base légale"><SelectField value={form.baseLegale} onChange={set("baseLegale")} options={BASE_LEGALES} /></FieldGroup>
            <FieldGroup label="Responsable suivi"><SelectField value={form.responsableId} onChange={set("responsableId")} options={personneOptions} /></FieldGroup>
            <FieldGroup label="Statut DPA"><SelectField value={form.dpaStatut} onChange={set("dpaStatut")} options={DPA_STATUTS} /></FieldGroup>
            <FieldGroup label="Échéance DPA"><Input type="date" value={form.dpaEcheance} onChange={set("dpaEcheance")} /></FieldGroup>

            <div style={{ gridColumn: "span 2" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: form.horsEEE ? "#fef2f2" : "#f0fdf4", borderRadius: RADIUS.md, border: `1.5px solid ${form.horsEEE ? "#fca5a5" : "#86efac"}` }}>
                <input type="checkbox" checked={form.horsEEE} onChange={setCheck("horsEEE")} id="horsEEE" style={{ width: 16, height: 16, cursor: "pointer" }} />
                <label htmlFor="horsEEE" style={{ fontSize: 13, fontWeight: 600, color: form.horsEEE ? "#991b1b" : "#065f46", cursor: "pointer" }}>
                  {form.horsEEE ? "Transfert hors EEE — CCT / BCR obligatoires" : "Traitement dans l'EEE — pas de transfert hors zone"}
                </label>
              </div>
            </div>

            {form.horsEEE && (
              <>
                <div style={{ gridColumn: "span 2" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", background: "#fff7ed", borderRadius: RADIUS.md, border: "1.5px solid #fed7aa", marginBottom: 8 }}>
                    <input type="checkbox" checked={form.sccRequis} onChange={setCheck("sccRequis")} id="sccRequis" style={{ width: 16, height: 16, cursor: "pointer" }} />
                    <label htmlFor="sccRequis" style={{ fontSize: 12, fontWeight: 600, color: "#c2410c", cursor: "pointer" }}>CCT (SCC) requises pour ce transfert</label>
                  </div>
                </div>
                {form.sccRequis && (
                  <>
                    <FieldGroup label="Statut CCT / SCC"><SelectField value={form.sccStatut} onChange={set("sccStatut")} options={SCC_STATUTS} /></FieldGroup>
                    <FieldGroup label="Statut TIA"><SelectField value={form.tiaStatut} onChange={set("tiaStatut")} options={TIA_STATUTS} /></FieldGroup>
                  </>
                )}
              </>
            )}

            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Preuve associée"><SelectField value={form.preuveRef} onChange={set("preuveRef")} options={preuveOptions} /></FieldGroup>
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Commentaires"><Textarea value={form.commentaires} onChange={set("commentaires")} placeholder="Notes, certifications, points de vigilance…" rows={2} /></FieldGroup>
            </div>
          </div>
        </FormModal>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {filtered.map(st => {
          const rc = risqueColor[st.niveauRisque] || risqueColor["Modéré"];
          const tc = st.horsEEE ? transfertColor[true] : transfertColor[false];
          const resp = personnes.find(p => p.id === st.responsableId || p.id === Number(st.responsableId));
          const hc = resp ? (habColor[resp.habilitation] || habColor["Autre"]) : null;
          const dpaAlert = st.dpaEcheance && new Date(st.dpaEcheance) < new Date(Date.now() + 90 * 86400000);
          const isHighlighted = st.id === highlightId || Number(st.id) === highlightId;
          return (
            <div
              key={st.id}
              ref={isHighlighted ? highlightRef : null}
              style={{
                background: isHighlighted ? "#fffbeb" : COLORS.white,
                borderRadius: RADIUS.lg,
                border: `1.5px solid ${isHighlighted ? "#f59e0b" : st.horsEEE ? "#fca5a5" : COLORS.border}`,
                padding: 16,
                boxShadow: isHighlighted ? "0 0 0 2px #fef3c7" : "0 1px 4px rgba(0,0,0,0.04)",
                transition: "box-shadow 0.15s",
              }}
              onMouseEnter={e => !isHighlighted && (e.currentTarget.style.boxShadow = "0 4px 16px rgba(37,99,235,0.09)")}
              onMouseLeave={e => !isHighlighted && (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)")}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: COLORS.textPrimary, marginBottom: 5 }}><HighlightText text={st.nom} term={searchTerm} /></div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 99, background: "#f1f5f9", color: "#374151", border: `1px solid ${COLORS.border}`, fontWeight: 600 }}>{st.pays}</span>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 99, background: tc.bg, color: tc.text, border: `1px solid ${tc.border}`, fontWeight: 700 }}>{tc.label}</span>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 99, background: rc.bg, color: rc.text, border: `1px solid ${rc.border}`, fontWeight: 700 }}>Risque {st.niveauRisque}</span>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 99, background: st.dpaStatut === "Signé" ? "#d1fae5" : "#fee2e2", color: st.dpaStatut === "Signé" ? "#065f46" : "#991b1b", border: `1px solid ${st.dpaStatut === "Signé" ? "#6ee7b7" : "#fca5a5"}`, fontWeight: 700 }}>DPA : {st.dpaStatut}</span>
                    {st.horsEEE && st.sccRequis && (
                      <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 99, fontWeight: 700, background: (st.sccStatut || "").includes("Activées") ? "#d1fae5" : "#fee2e2", color: (st.sccStatut || "").includes("Activées") ? "#065f46" : "#991b1b", border: `1px solid ${(st.sccStatut || "").includes("Activées") ? "#6ee7b7" : "#fca5a5"}` }}>CCT : {st.sccStatut || "—"}</span>
                    )}
                    {st.horsEEE && st.sccRequis && st.tiaStatut && (
                      <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 99, fontWeight: 700, background: st.tiaStatut === "Réalisé" ? "#d1fae5" : "#fef3c7", color: st.tiaStatut === "Réalisé" ? "#065f46" : "#92400e", border: `1px solid ${st.tiaStatut === "Réalisé" ? "#6ee7b7" : "#fcd34d"}` }}>TIA : {st.tiaStatut}</span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <button onClick={() => { setEditing(st); setForm({ ...st }); }} style={{ padding: `${SPACING.xs}px 9px`, borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.primaryAccent}`, background: "transparent", color: COLORS.primaryAccent, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdEdit size={14} /></button>
                    <button onClick={() => setSousTraitants(ss => ss.filter(x => x.id !== st.id))} style={{ padding: "4px 9px", borderRadius: RADIUS.sm, border: "1.5px solid #fee2e2", background: "#fff5f5", color: "#dc2626", fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdDelete size={14} /></button>
                </div>
              </div>

              <div style={{ fontSize: 12, color: "#374151", marginBottom: 8 }}>
                <strong>Service :</strong> {st.typeService} — <strong>Données :</strong> {st.donneestraites}
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", fontSize: 11 }}>
                {resp && hc && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 22, height: 22, borderRadius: "50%", background: hc.bg, border: `1.5px solid ${hc.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: hc.text }}>
                      {resp.prenom[0]}{resp.nom[0]}
                    </div>
                    <span style={{ color: "#374151" }}>{resp.prenom} {resp.nom}</span>
                    <HabBadge habilitation={resp.habilitation} />
                  </div>
                )}
                {st.dpaEcheance && (
                  <span style={{ color: dpaAlert ? "#dc2626" : "#6b7280", fontWeight: dpaAlert ? 700 : 400 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>{dpaAlert ? <MdWarningAmber size={13} /> : <MdCalendarToday size={13} />}{dpaAlert ? "DPA expire bientôt :" : "DPA jusqu'au"} {formatDateFr(st.dpaEcheance)}</span>
                  </span>
                )}
                {st.preuveRef && <span style={{ color: "#2563eb", display: "inline-flex", alignItems: "center", gap: 4 }}><MdLink size={13} /> {st.preuveRef}</span>}
              </div>
              {st.commentaires && (
                <div style={{ marginTop: 8, padding: "6px 10px", background: st.horsEEE ? "#fef9c3" : "#f8fafc", borderRadius: RADIUS.sm, fontSize: 11, color: st.horsEEE ? "#78350f" : "#6b7280" }}>
                  {st.commentaires}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Onglets Configuration → Référentiels (sans sous-traitants ni RACI — pages Gouvernance dédiées). */
const REFERENTIELS_CONFIG_TAB_KEYS = new Set(["personnes", "categories", "activites", "doctypes"]);

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────────────

export default function PageReferentiels({
  personnes,
  setPersonnes,
  categories,
  setCategories,
  activites,
  setActivites,
  onBack,
  onCreatePersonne,
  onUpdatePersonne,
  onDeletePersonne,
  searchHighlight,
  /** Onglet imposé par la navigation latérale (personnes, categories, activites, doctypes). */
  navTab = "personnes",
}) {
  const [tab, setTab] = useState(() => (REFERENTIELS_CONFIG_TAB_KEYS.has(navTab) ? navTab : "personnes"));

  useEffect(() => {
    setTab(REFERENTIELS_CONFIG_TAB_KEYS.has(navTab) ? navTab : "personnes");
  }, [navTab]);

  useEffect(() => {
    const itemType = searchHighlight?.itemType;
    if (itemType === "activite") setTab("activites");
    else if (itemType === "personne") setTab("personnes");
    else if (searchHighlight?.id != null) {
      const hid = Number(searchHighlight.id);
      if (personnes.some(p => p.id === hid || Number(p.id) === hid)) setTab("personnes");
      else if (activites.some(a => a.id === hid || Number(a.id) === hid)) setTab("activites");
    }
  }, [searchHighlight?.id, searchHighlight?.itemType, personnes, activites]);

  const tabs = [
    { key: "personnes", label: "Responsables" },
    { key: "categories", label: "Catégories" },
    { key: "activites", label: "Activités" },
    { key: "doctypes", label: "Types docs RGPD" },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Référentiels transversaux"
        onBack={onBack}
        backLabel="← Retour aux preuves"
      />

      <PageContent>
        <div style={{ background: "#eff6ff", border: "1.5px solid #bfdbfe", borderRadius: RADIUS.lg, padding: "12px 18px", marginBottom: 22, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ fontSize: 18, display: "inline-flex", alignItems: "center" }}><MdInfo size={18} /></span>
          <div style={{ fontSize: 13, color: "#1e40af", lineHeight: 1.6 }}>
            Ce référentiel rassemble les données partagées par les modules RGPD&nbsp;: <strong>responsables</strong>, <strong>catégories</strong> de preuves, <strong>types de documents</strong> pour l&apos;audit, et <strong>activités</strong> (liées à la matrice RACI). Le <strong>registre sous-traitants</strong> et la <strong>matrice RACI</strong> se gèrent sous <strong>Gouvernance RGPD</strong>.
          </div>
        </div>

        <div style={{ display: "flex", gap: 4, marginBottom: 22, background: COLORS.border, padding: 4, borderRadius: RADIUS.lg, width: "fit-content", flexWrap: "wrap" }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: "8px 18px", borderRadius: RADIUS.md, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", background: tab === t.key ? COLORS.white : "transparent", color: tab === t.key ? COLORS.primary : COLORS.textSecondary, boxShadow: tab === t.key ? "0 1px 4px rgba(0,0,0,0.08)" : "none", transition: "all 0.15s" }}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ background: COLORS.white, borderRadius: RADIUS.xl, border: `1.5px solid ${COLORS.border}`, padding: 24, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
          {tab === "personnes" && (
            <RefPersonnes
              personnes={personnes}
              setPersonnes={setPersonnes}
              onCreatePersonne={onCreatePersonne}
              onUpdatePersonne={onUpdatePersonne}
              onDeletePersonne={onDeletePersonne}
              searchHighlight={searchHighlight}
            />
          )}
          {tab === "categories" && <RefCategories categories={categories} setCategories={setCategories} />}
          {tab === "doctypes" && <RefDocTypes />}
          {tab === "activites" && <RefActivitesCatalog activites={activites} setActivites={setActivites} categories={categories} personnes={personnes} searchHighlight={searchHighlight} setCategories={setCategories} />}
        </div>
      </PageContent>
    </PageShell>
  );
}

export function PageRegistreSousTraitants({ sousTraitants, setSousTraitants, personnes, proofs, searchHighlight, onBack }) {
  return (
    <PageShell>
      <PageHeader
        title="Registre sous-traitants"
        subtitle="Gouvernance RGPD"
        onBack={onBack}
        backLabel="← Tableau de bord"
      />
      <PageContent>
        <div style={{ background: "#eff6ff", border: "1.5px solid #bfdbfe", borderRadius: RADIUS.lg, padding: "12px 18px", marginBottom: 22, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ fontSize: 18, display: "inline-flex", alignItems: "center" }}><MdInfo size={18} /></span>
          <div style={{ fontSize: 13, color: "#1e40af", lineHeight: 1.6 }}>
            Prestataires traitant des données pour votre compte (art.&nbsp;28 RGPD). Les listes déroulantes des fiches ROPA et preuves s&apos;appuient sur ce registre.
          </div>
        </div>
        <div style={{ background: COLORS.white, borderRadius: RADIUS.xl, border: `1.5px solid ${COLORS.border}`, padding: 24, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
          <RefSousTraitants sousTraitants={sousTraitants} setSousTraitants={setSousTraitants} personnes={personnes} proofs={proofs} searchHighlight={searchHighlight} />
        </div>
      </PageContent>
    </PageShell>
  );
}

export function PageMatriceRaci({ activites, personnes, raciMatrix, setRaciMatrix, categories, onBack }) {
  return (
    <PageShell>
      <PageHeader
        title="Matrice RACI"
        subtitle="Gouvernance RGPD"
        onBack={onBack}
        backLabel="← Tableau de bord"
      />
      <PageContent>
        <div style={{ background: "#eff6ff", border: "1.5px solid #bfdbfe", borderRadius: RADIUS.lg, padding: "12px 18px", marginBottom: 22, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ fontSize: 18, display: "inline-flex", alignItems: "center" }}><MdInfo size={18} /></span>
          <div style={{ fontSize: 13, color: "#1e40af", lineHeight: 1.6 }}>
            Les lignes reprennent les <strong>activités RGPD</strong> (Configuration → Référentiels → Activités) et les colonnes les <strong>responsables</strong>.
          </div>
        </div>
        <div style={{ background: COLORS.white, borderRadius: RADIUS.xl, border: `1.5px solid ${COLORS.border}`, padding: 24, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
          <RaciMatrix activites={activites} categories={categories} personnes={personnes} matrix={raciMatrix} setMatrix={setRaciMatrix} />
        </div>
      </PageContent>
    </PageShell>
  );
}
